import React from "react";
import useFlickerEffect from "../../hooks/useFlickerEffect.ts";
import { useContactForm } from "../../hooks/useContactForm.ts";
import contactUsBg from "../../assets/images/ContactContainer.svg";
import contactUsBgMobile from "../../assets/images/contact-720.png";
import SuccessModal from "../ui/SuccessModal.tsx";
import FormField from "../ui/FormField.tsx";
import CountrySelect from "../ui/CountrySelect.tsx";
import MessageTextarea from "../ui/MessageTextarea.tsx";
import ConsentCheckbox from "../ui/ConsentCheckbox.tsx";
import SubmitButton from "../ui/SubmitButton.tsx";

const ContactUs = () => {
  const activeClass = useFlickerEffect();

  const {
    // Form state
    fullName,
    setFullName,
    businessEmail,
    setBusinessEmail,
    jobTitle,
    setJobTitle,
    companyName,
    setCompanyName,
    country,
    setCountry,
    message,
    setMessage,
    consent,
    setConsent,
    
    // UI state
    errors,
    touched,
    focused,
    setFocused,
    showSuccess,
    setShowSuccess,
    isSubmitting,
    
    // Computed values
    fieldTextStyle,
    baseFieldStyle,
    charactersLeft,
    isFormValid,
    MAX_MESSAGE_LEN,
    
    // Functions
    setAndValidate,
    setTouched,
    handleSubmit,
  } = useContactForm();

  return (
    <>
      <section id="contactussection" className="relative py-12">
        <div className="container">
          <div className="relative mx-auto max-w-[980px] px-4 md:px-8 py-8 md:py-12 pt-1 pr-4" style={{paddingTop:"15px"}}>
            {/* Mobile Background Container - Full Section Coverage */}
            <div className="md:hidden absolute inset-0 -z-10">
              <img
                src={contactUsBgMobile}
                alt="Contact background mobile"
                className="w-full h-full"
                style={{
                  borderRadius: '12px',
                  height: '720px'
                }}
              />
            </div>
            
            <SuccessModal 
              isOpen={showSuccess}
              onClose={() => setShowSuccess(false)}
              fieldTextStyle={fieldTextStyle}
            />
            
            {/* Desktop Background Container */}
            <div className="hidden md:block pointer-events-none absolute -inset-x-6 -top-2 -z-10">
              <img
                src={contactUsBg}
                alt="Contact background"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Form Container */}
            <div 
              className="relative mt-[33px] md:mt-2"
              style={{
                borderRadius: '12px',
              }}
            >
              <h2
                className={`text-white text-[20px] md:text-[24px] font-gothicproMedium ${activeClass} md:pt-4 mb-4 md:mb-4`}
                style={{ lineHeight: "110%", letterSpacing: "0.01em" }}
              >
                Get your exclusive live demo and see Armory in action!
              </h2>
              <p
                className="mb-4 md:mb-6 text-[16px] md:text-[16px]"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  lineHeight: "140%",
                  letterSpacing: 0,
                  color: "#C9C9C9",
                }}
              >
                Discover how Armory safeguards your organization with cutting-edge
                technology.
              </p>

              <form onSubmit={handleSubmit} className="pb-4 md:pb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <FormField
                    id="fullName"
                    name="name"
                    value={fullName}
                    onChange={setFullName}
                    placeholder="Full Name*"
                    required
                    autoComplete="name"
                    baseFieldStyle={baseFieldStyle}
                    fieldTextStyle={fieldTextStyle}
                    errors={errors}
                    touched={touched}
                    focused={focused}
                    setFocused={setFocused}
                    setAndValidate={setAndValidate}
                    setTouched={setTouched}
                  />
                  
                  <FormField
                    id="businessEmail"
                    name="email"
                    type="email"
                    value={businessEmail}
                    onChange={setBusinessEmail}
                    placeholder="Business Email*"
                    required
                    autoComplete="email"
                    baseFieldStyle={baseFieldStyle}
                    fieldTextStyle={fieldTextStyle}
                    errors={errors}
                    touched={touched}
                    focused={focused}
                    setFocused={setFocused}
                    setAndValidate={setAndValidate}
                    setTouched={setTouched}
                  />
                  
                  <FormField
                    id="jobTitle"
                    name="jobTitle"
                    value={jobTitle}
                    onChange={setJobTitle}
                    placeholder="Job Title*"
                    required
                    baseFieldStyle={baseFieldStyle}
                    fieldTextStyle={fieldTextStyle}
                    errors={errors}
                    touched={touched}
                    focused={focused}
                    setFocused={setFocused}
                    setAndValidate={setAndValidate}
                    setTouched={setTouched}
                  />
                  
                  <FormField
                    id="companyName"
                    name="company"
                    value={companyName}
                    onChange={setCompanyName}
                    placeholder="Company Name*"
                    required
                    baseFieldStyle={baseFieldStyle}
                    fieldTextStyle={fieldTextStyle}
                    errors={errors}
                    touched={touched}
                    focused={focused}
                    setFocused={setFocused}
                    setAndValidate={setAndValidate}
                    setTouched={setTouched}
                  />
                  
                  <CountrySelect
                    value={country}
                    onChange={setCountry}
                    baseFieldStyle={baseFieldStyle}
                    fieldTextStyle={fieldTextStyle}
                    errors={errors}
                    touched={touched}
                    focused={focused}
                    setFocused={setFocused}
                    setAndValidate={setAndValidate}
                    setTouched={setTouched}
                  />
              </div>

                <MessageTextarea
                    value={message}
                  onChange={setMessage}
                    onFocus={() => setFocused("message")}
                  onBlur={() => setFocused(focused === "message" ? null : focused)}
                  fieldTextStyle={fieldTextStyle}
                  maxLength={MAX_MESSAGE_LEN}
                  focused={focused}
                  setFocused={setFocused}
                />

                <ConsentCheckbox
                  checked={consent}
                  onChange={setConsent}
                  fieldTextStyle={fieldTextStyle}
                  errors={errors}
                  touched={touched}
                  setAndValidate={setAndValidate}
                />

                <SubmitButton
                  isFormValid={isFormValid}
                  isSubmitting={isSubmitting}
                  errors={errors}
                  fieldTextStyle={fieldTextStyle}
                />
            </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactUs;
